import React, { useEffect, useRef, useState } from "react";
import AuthLayout from "../../layout/auth";
import { useApiCall, ApiEndpoints } from "../../api";
import Loading from "../../component/loading";
import { appRoutes, useAppNavigation } from "../../router/routes";
import { storageApi } from "../../storage/storageApi";
import { SelectOption } from "../../component/selectOption";
import { Input } from "../../component/input";
import {
  BASE_CAPTCHA_IMAGE_URL,
  RESEND_TOP_INTERVAL_IN_SECONDS,
} from "../../config";
import { Tooltip } from "../../component/Tooltip";
import { useValidation, ValidationTypes } from "../../helper/validation";
import { useAlert } from "react-alert";
import { AiOutlineReload } from "react-icons/ai";

const LoginPage = () => {
  const { navigate } = useAppNavigation();
  const loginUser = storageApi.useLogin();

  const alert = useAlert();

  const [isNextStepActive, setIsNextStepActive] = useState(false);
  const [step, setStep] = useState(0);

  const [inputPhoneNumber, setInputPhoneNumber] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [captchaValue, setCaptchaValue] = useState("");

  const [verifyCode, setVerifyCode] = useState("");

  const [fullName, setFullName] = useState("");
  const [landlineNumber, setLandlineNumber] = useState("");
  const [sectionType, setSectionType] = useState("");
  const [structureType, setStructureType] = useState("");
  const [assistance, setAssistance] = useState("");
  const [state, setState] = useState("");
  const [city, setCity] = useState("");
  const [department, setDepartment] = useState("");
  const [organization, setOrganization] = useState("");

  const loginValidation = useValidation([
    {
      name: "captchaValue",
      label: "کد امنیتی",
      value: captchaValue,
      validations: [
        {
          type: ValidationTypes.stringRequired,
        },
      ],
    },
  ]);

  const { validate, errors, removeError } = useValidation([
    ...(structureType === "ostani"
      ? [
          {
            name: "state",
            label: "استان",
            value: state,
            validations: [
              {
                type: ValidationTypes.stringRequired,
              },
            ],
          },
          {
            name: "city",
            label: "شهر",
            value: city,
            validations: [
              {
                type: ValidationTypes.stringRequired,
              },
            ],
          },
        ]
      : []),
    {
      name: "sectionType",
      label: "نوع دستگاه",
      value: sectionType,
      validations: [
        {
          type: ValidationTypes.objectRequired,
        },
      ],
    },
    {
      name: "structureType",
      label: "ساختار دستگاه تابعه",
      value: structureType,
      validations: [
        {
          type: ValidationTypes.objectRequired,
        },
      ],
    },
    {
      name: "department",
      label: "نام مجموعه",
      value: department,
      validations: [
        {
          type: ValidationTypes.stringRequired,
        },
      ],
    },
    {
      name: "organization",
      label: "نام سازمان",
      value: organization,
      validations: [
        {
          type: ValidationTypes.stringRequired,
        },
      ],
    },
    {
      name: "assistance",
      label: "معاونت/مدیریت/ مدرسه و ...",
      value: assistance,
      validations: [
        {
          type: ValidationTypes.stringRequired,
        },
        {
          type: ValidationTypes.onlyPersianCharacters,
        },
      ],
    },
    {
      name: "fullName",
      label: "نام و نام خانوادگی",
      value: fullName,
      validations: [
        {
          type: ValidationTypes.stringRequired,
        },
        {
          type: ValidationTypes.onlyPersianCharacters,
        },
      ],
    },
    {
      name: "landlineNumber",
      label: "شماره تماس",
      value: landlineNumber,
      validations: [
        {
          type:
            structureType === "setadi_markazi"
              ? ValidationTypes.onlyStartsWith021
              : ValidationTypes.onlyStartsWithout09,
        },
        {
          type: ValidationTypes.stringMaxLength,
          options: {
            length: 11,
          },
        },
        {
          type: ValidationTypes.stringMinLength,
          options: {
            length: 8,
          },
        },
      ],
    },
  ]);

  const [
    [cityList, stateList, organizationList, departmentList],
    { loading: signupFormLoading, fetch: fetchSignupForm },
  ] = useApiCall({
    endpoints: [
      ApiEndpoints.user.cityList,
      ApiEndpoints.user.stateList,
      ApiEndpoints.user.organizationList,
      ApiEndpoints.user.departmentList,
    ],
    lazy: true,
  });

  // اصلاح نام متغیرها برای api.js
  const [, { loading: signupLoading, fetch: signup }] = useApiCall({
    endpoints: [ApiEndpoints.user.signup],
    payload: {
      phoneNumber,
      landlineNumber,
      fullName,
      sectionType,
      structureType,
      assistance,
      state,
      city,
      department,
      organization,
    },
    lazy: true,
  });

  const [, { loading: updateProfileLoading, fetch: updateProfile }] =
    useApiCall({
      endpoints: [ApiEndpoints.user.updateProfile],
      payload: {
        phoneNumber,
        landlineNumber,
        fullName,
        sectionType,
        structureType,
        assistance,
        state,
        city,
        department,
        organization,
      },
      lazy: true,
    });

  const sectionList = [
    {
      id: "keshvari",
      title: "کشوری",
    },
    {
      id: "lashkari",
      title: "لشکری",
    },
  ];
  const structureList = [
    {
      id: "setadi_markazi",
      title: "ستادی/مرکزی",
    },
    {
      id: "ostani",
      title: "استانی",
    },
  ];

  const [, { fetch: fetchProfile, loading: profileLoading }] = useApiCall({
    endpoints: ApiEndpoints.user.profile,
    lazy: true,
  });

  const [
    [captchaResponse],
    { fetch: fetchNewCaptcha, loading: loadingCaptcha },
  ] = useApiCall({
    endpoints: ApiEndpoints.captcha.get,
    lazy: false,
  });
  
  const captchaData = captchaResponse?.data; 
  const captchaImageUrl = captchaData?.image_url;
  const captchaKey = captchaData?.captcha_key;
  const [isCaptchaImageLoaded, setIsCaptchaImageLoaded] = useState(false);

  // اصلاح نام متغیرها برای api.js
  const [
    [loginResponse],
    { fetch: fetchOtpGenerate, loading: loadingOtpGenerate },
  ] = useApiCall({
    endpoints: ApiEndpoints.user.requestOtp,
    payload: {
      phoneNumber,
      captchaKey,
      captchaValue,
    },
    lazy: true,
  });

  // اصلاح نام متغیرها برای api.js
  const [
    [verificationResponse],
    { fetch: fetchOtpVerify, loading: loadingOtpVerify },
  ] = useApiCall({
    endpoints: ApiEndpoints.user.verifyOtp,
    payload: {
      phoneNumber,
      otpCode: verifyCode,
    },
    lazy: true,
  });

  const [, { fetch: fetchOtpVerifyNew, loading: loadingOtpVerifyNew }] =
    useApiCall({
      endpoints: ApiEndpoints.user.verifyNewOtp,
      payload: {
        phoneNumber,
        otpCode: verifyCode,
      },
      lazy: true,
    });

  const isLoading =
    loadingOtpVerifyNew ||
    loadingOtpVerify ||
    loadingOtpGenerate ||
    profileLoading;

  useEffect(() => {
    if (step === 0) {
      setIsNextStepActive(phoneNumber.toString().length === 11);
    } else if (step === 1) {
      setIsNextStepActive(verifyCode.toString().length === 6);
    } else if (step === 2) {
      setIsNextStepActive(true);
    }
  }, [verifyCode, phoneNumber, step]);

  const handleOnVerifyOtpChange = (event) => {
    const value = event.target.value;
    setVerifyCode(value);
  };

  const [isTimerEnd, setIsTimerEnd] = useState(false);
  const timerIntervalRef = useRef(null);
  const [timer, setTimer] = useState(0);

  useEffect(() => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }
  }, []);

  const onTimerEnd = () => {
    setIsTimerEnd(true);
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }
  };

  const startTime = () => {
    setIsTimerEnd(false);
    setTimer(RESEND_TOP_INTERVAL_IN_SECONDS);

    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
    }

    timerIntervalRef.current = setInterval(() => {
      setTimer((c) => {
        if (c <= 0) {
          onTimerEnd();
          clearInterval(timerIntervalRef.current);
          return 0;
        }
        return c - 1;
      });
    }, 1000);
  };

  const handleSubmit = async () => {
    const isUserForceUpdateProfile =
      loginResponse?.data?.isUserForceUpdateProfile;
    const isUserNotFound = loginResponse?.data?.isUserNotFound;

    if (step === 0) {
      const { isValid } = loginValidation.validate();
      if (!isValid) {
        return alert.error("لطفا خطا های فرم را برطرف کنید");
      }
      const [loginResult] = await fetchOtpGenerate();
      if (loginResult.isOk) {
        if (loginResult.data.otpCode && loginResult.data.otpCode !== "*") {
          alert.success("code: " + loginResult.data.otpCode);
        }
        setStep(1);
        setIsNextStepActive(false);
        startTime();
      }
    } else if (step === 1) {
      const [fetchVerifyOtp] = isUserNotFound
        ? await fetchOtpVerifyNew()
        : await fetchOtpVerify();

      if (fetchVerifyOtp.isOk) {
        if (!isUserNotFound) {
          await loginUser({
            ...fetchVerifyOtp.data,
            isUserForceUpdateProfile,
          });
          const [profile] = await fetchProfile();
          if (profile.isOk) {
            loginUser({
              ...profile.data,
              ...fetchVerifyOtp.data,
              isUserForceUpdateProfile,
            });
          }
          if (!isUserForceUpdateProfile) {
            navigate(appRoutes.dashboard);
          } else {
            setFullName(profile.data.fullName);
            setLandlineNumber(profile.data.landlineNumber);
            setSectionType(profile.data.sectionType);
            setStructureType(profile.data.structureType);
            setAssistance(profile.data.assistance);
            setState(profile.data.state);
            setCity(profile.data.city);
            setDepartment(profile.data.department);
            setOrganization(profile.data.organization);

            setStep(2);
            await fetchSignupForm();
          }
        } else {
          setStep(2);
          await fetchSignupForm();
        }
      }
    } else if (step === 2) {
      const { isValid } = validate();
      if (!isValid) {
        return alert.error("لطفا خطا های فرم را برطرف کنید");
      }

      const [signupResult] = isUserNotFound
        ? await signup()
        : await updateProfile({
            userId: verificationResponse.data.userId,
          });

      console.log({ signupResult });

      if (signupResult.isOk) {
        if (isUserNotFound) {
          await loginUser({
            ...signupResult.data,
          });
        }

        const [profile] = await fetchProfile();
        if (profile.isOk) {
          loginUser({
            ...profile.data,
            ...(isUserNotFound ? signupResult.data : verificationResponse.data),
          });
        }
        navigate(appRoutes.dashboard);
      }
    }
  };

  const onGoToEnterPhoneNumberClick = () => {
    setStep(0);
    setVerifyCode("");
    setCaptchaValue("");
    fetchNewCaptcha();
  };

  const onResendOtpClick = async () => {
    onTimerEnd();
    const [loginResult] = await fetchOtpGenerate();
    if (loginResult.isOk) {
      if (loginResult.data.otpCode) {
        alert.success("code: " + loginResult.data.otpCode);
      }
    }
    startTime();
  };

  const renderTimeMinutesAndSeconds = () => {
    const minutes = parseInt(timer / 60);
    const seconds = timer % 60;
    if (minutes > 0) {
      return `${minutes}:${seconds} دقیقه `;
    }
    if (seconds > 0) {
      return `${seconds} ثانیه`;
    }
    return "";
  };

  const renderOtpResend = () => {
    if (isTimerEnd) {
      return (
        <a className={"cursor-pointer"} onClick={onResendOtpClick}>
          دریافت مجدد
        </a>
      );
    } else {
      return (
        <div>
          کد را دریافت نکرده اید؟ (ارسال مجدد تا
          {" " + renderTimeMinutesAndSeconds() + " "}
          دیگر)
        </div>
      );
    }
  };

  const renderOtp = () => {
    return (
      <>
        <p className="text-center text-md leading-8 mb-4">
          کد فعالسازی ۶ رقمی برای شماره {phoneNumber} ارسال شده است.
        </p>
        <div className="w-full flex flex-col gap-8 items-center ">
          <div className="form-field my-form-field w-full relative">
            <div className="text-center">
              <input
                type="number"
                className="rounded-lg pl-2 text-center tracking-widest  border-[1.5px] border-gray-100 w-full text-sm  input"
                placeholder="کد اعتبار سنجی را وارد کنید"
                value={verifyCode}
                onChange={handleOnVerifyOtpChange}
              />
            </div>
          </div>
          <button
            onClick={handleSubmit}
            className={`mt-4 mb-4 pb-4 md:w-[60%] w-full flex items-center justify-center bg-blue-500 text-white py-4 rounded-lg ${isNextStepActive ? "opacity-100" : "opacity-40"} `}
          >
            اعتبارسنجی و ادامه
            <Loading isLoading={isLoading} />
          </button>
          <div>{renderOtpResend()}</div>
          <a
            className={"cursor-pointer pb-4"}
            onClick={onGoToEnterPhoneNumberClick}
          >
            تغییر شماره تلفن
          </a>
        </div>
      </>
    );
  };

  const renderEnterPhone = () => {
    return (
      <>
        <div className="form-field w-full relative ">
          <Input
            label={"شماره همراه"}
            type="number"
            placeholder="شماره تلفن خود را وارد کنید"
            value={inputPhoneNumber}
            onChange={(p) => {
              const phone = String(p).replace("+98", "");
              setPhoneNumber(phone.startsWith("0") ? phone : `0${phone}`);
              setInputPhoneNumber(String(p));
            }}
          />
        </div>

        <div className={"flex flex-row items-center justify-center mt-4"}>
          {/* رفع خطای لود اولیه با جایگذاری undefined برای آدرس خالی */}
          <img
            onLoad={() => setIsCaptchaImageLoaded(true)}
            src={captchaImageUrl ? BASE_CAPTCHA_IMAGE_URL + captchaImageUrl : undefined}
            className={`${!isCaptchaImageLoaded ? "hidden" : ""}`}
            alt="کپچا"
          />
          <Loading
            isLoading={loadingCaptcha || !isCaptchaImageLoaded}
            color={"blue"}
          />
          <div
            className={"bg-white mx-2 cursor-pointer"}
            onClick={() => {
              fetchNewCaptcha();
              setIsCaptchaImageLoaded(false);
            }}
          >
            <AiOutlineReload />
          </div>
        </div>

        <div className="form-field w-full relative ">
          <Input
            label={"کد امنیتی"}
            onChange={(val) => {
              setCaptchaValue(val);
              loginValidation.removeError("captchaValue");
            }}
            placeholder={"کد امنیتی را وارد کنید"}
            error={loginValidation.errors["captchaValue"]}
          />
        </div>
        <button
          onClick={handleSubmit}
          className={`mt-4 mb-4 pb-4 md:w-[60%] w-full flex items-center justify-center bg-blue-500 text-white py-4 rounded-lg ${isNextStepActive ? "opacity-100" : "opacity-40"} `}
        >
          دریافت کد یکبارمصرف <Loading isLoading={isLoading} />
        </button>
      </>
    );
  };

  const tooltip =
    "راهنما : در صورت استقرار در دستگاه مرکزی ( وزارتخانه/ سازمان مرکزی ( مستقر در تهران)) که در ساختار، استانی محسوب نمی شوید باید ستادی/مرکزی را انتخاب نمایید.";

  const renderSignup = () => {
    if (signupFormLoading) {
      return <Loading isLoading={true} center />;
    }
    return (
      <>
        <p className="text-center text-md leading-8 mb-4">
          اطلاعات پایه خود را تکمیل کنید
        </p>

        <div className="grid md:grid-cols-2 gap-4 w-full">
          <div className="form-field w-full relative">
            <SelectOption
              label={"نوع دستگاه"}
              value={sectionType}
              options={sectionList.map((c) => ({
                label: c.title,
                value: c.id,
              }))}
              onChange={(e) => {
                setSectionType(e.value);
                removeError("sectionType");
              }}
              error={errors["sectionType"]}
            />
          </div>
          <div className="form-field w-full relative">
            <SelectOption
              label={"ساختار دستگاه تابعه"}
              labelComponent={<Tooltip tooltip={tooltip} />}
              value={structureType}
              options={structureList.map((c) => ({
                label: c.title,
                value: c.id,
              }))}
              onChange={(e) => {
                setStructureType(e.value);
                setOrganization(null);
                removeError("structureType");
              }}
              error={errors["structureType"]}
            />
          </div>

          {structureType === "ostani" && (
            <div className="form-field w-full relative ">
              <SelectOption
                label={"استان"}
                value={state}
                options={stateList?.data?.map((c) => ({
                  label: c.name,
                  value: c.id,
                }))}
                onChange={(e) => {
                  setState(e.value);
                  setCity(null);
                  removeError("state");
                }}
                error={errors["state"]}
              />
            </div>
          )}

          {structureType === "ostani" && (
            <div className="form-field w-full relative ">
              <SelectOption
                label={"شهر"}
                value={city}
                options={cityList?.data
                  ?.filter((c) => c.state === state)
                  ?.map((c) => ({
                    label: c.name,
                    value: c.id,
                  }))}
                onChange={(e) => {
                  setCity(e.value);
                  removeError("city");
                }}
                error={errors["city"]}
              />
            </div>
          )}

          <div className="form-field w-full relative ">
            <SelectOption
              label={"نام مجموعه"}
              value={department}
              options={departmentList?.data
                ?.filter((c) => c.dastgah_type === sectionType)
                ?.map((c) => ({
                  label: c.title,
                  value: c.id,
                }))}
              onChange={(e) => {
                setDepartment(e.value);
                removeError("department");
              }}
              error={errors["department"]}
            />
          </div>
          <div className="form-field w-full relative ">
            <SelectOption
              label={"نام سازمان"}
              value={organization}
              options={organizationList?.data
                ?.filter((c) => {
                  return (
                    c.sakhtar_dastgah_tabee_type === structureType &&
                    c.majmooe === department
                  );
                })
                ?.map((c) => ({
                  label: c.title,
                  value: c.id,
                }))}
              onChange={(e) => {
                setOrganization(e.value);
                removeError("organization");
              }}
              error={errors["organization"]}
            />
          </div>
          <div className="form-field w-full relative ">
            <Input
              label={"معاونت/مدیریت/ مدرسه و ..."}
              onChange={(c) => {
                setAssistance(c);
                removeError("assistance");
              }}
              value={assistance}
              error={errors["assistance"]}
            />
          </div>
          <div className="form-field w-full relative ">
            <Input
              label={"نام و نام خانوادگی"}
              onChange={(c) => {
                setFullName(c);
                removeError("fullName");
              }}
              value={fullName}
              error={errors["fullName"]}
            />
          </div>
          <div className="form-field w-full relative ">
            <Input
              label={"شماره تماس"}
              onChange={(c) => {
                setLandlineNumber(
                  c === "" ? "" : c.startsWith("0") ? c : `0${c}`,
                );
                removeError("landlineNumber");
              }}
              type="string"
              value={landlineNumber}
              error={errors["landlineNumber"]}
              placeholder={"شماره ثابت به همراه کد شهر"}
            />
          </div>
          <div className="form-field w-full relative ">
            <Input
              disabled
              readOnly
              label={"شماره همراه"}
              onChange={(c) => setLandlineNumber(c)}
              value={phoneNumber}
            />
          </div>
        </div>

        <div className="w-full flex flex-col gap-8 items-center ">
          <button
            className="mt-4 mb-4 pb-4 md:w-[60%] flex items-center justify-center w-full bg-blue-500 text-white py-4 rounded-lg"
            onClick={handleSubmit}
          >
            {loginResponse?.data?.isUserForceUpdateProfile
              ? "بروزرسانی"
              : "ثبت نام"}
            <Loading
              isLoading={
                signupLoading || signupFormLoading || updateProfileLoading
              }
            />
          </button>
        </div>
      </>
    );
  };

  const renderBody = () => {
    if (step === 0) {
      return renderEnterPhone();
    } else if (step === 1) {
      return renderOtp();
    } else if (step === 2) {
      return renderSignup();
    }
  };

  return <AuthLayout>{renderBody()}</AuthLayout>;
};

export default LoginPage;